import { ensureGeofences } from "../../../../../../../services/useOrgData";
import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Box, Stack } from '@mui/material';
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { mkConfig, generateCsv, download } from "export-to-csv";
import { useParams } from "react-router";
import TopBar from "../../../../../../../component/TopBar";
import DataTable from "../../../../../../../component/DataTable";
import { attendanceDayColumns } from "../../dayColumns";
import MainBox from "../../../../../container/MainBox";
import BtnSecondary from "../../../../../../../component/SecondaryButton";
import { SearchInput, FilterSelect, StatusChip } from "../../../../../../../component/listing";
import { getAllDayAttendanceByEmployeeId } from "../../../../../../../services/facialRecognition";
import { fetchEmployeesByIdArray } from "../../../../../../../services/employee";

import { isPointInFence } from "../../../../../../../utils/isPointInFence";
import { getDuration } from "../../../../../../../utils/getDuration";
import { calculateTime } from "../../../../../../../utils/calculateTime";
import { useNotify } from "../../../../../../../component/NotificationProvider";
import { tokens, displayFont, radius } from "../../../../../../../theme";

const statusOf = (row = {}) => {
  if (row?.arrivalTime == null) return "Absent";
  if (row?.hasArrivedLate) return "Late";
  return "Present";
};
const STATUS_TONE = { Present: "success", Late: "warning", Absent: "danger" };
const initialsOf = (name) => (name || "?").split(" ").filter(Boolean).map((p) => p[0]).join("").slice(0, 2).toUpperCase();

export default function EmployeeAttendance() {
  const notify = useNotify();
  const { employeeId } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [fence, setFence] = useState();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const [attRes, empRes, fenceRes] = await Promise.all([
          getAllDayAttendanceByEmployeeId(employeeId),
          fetchEmployeesByIdArray([employeeId]),
          ensureGeofences().catch(() => null),
        ]);
        if (!active) return;
        if (attRes?.success === false) throw new Error(attRes?.message || "Failed to load attendance");
        setData(Array.isArray(attRes?.data) ? [...attRes.data].reverse() : []);
        setEmployee(empRes?.data?.[0] || null);
        setFence(fenceRes?.data?.[0]?.geofenceObj?.fences?.[0]);
      } catch (e) {
        if (active) { setError(true); notify.error(e?.message || "Failed to load attendance"); }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [employeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const name = employee ? `${employee.fname || ""} ${employee.lname || ""}`.trim() : "—";
  const role = employee ? [employee.designation, employee.employmentType].filter(Boolean).join(" · ") : "";
  const latestStatus = data[0] ? statusOf(data[0]) : null;

  const locationOf = (row) => {
    if (!fence || row?.arrivalLat == null || row?.arrivalLong == null) return "—";
    return isPointInFence(fence, row.arrivalLat, row.arrivalLong) ? "In Office" : "Remote";
  };
  const clockInOf = (row) => (row?.arrivalTime != null ? calculateTime(row.arrivalTime) : "--");
  const clockOutOf = (row) => (!isNaN(Number(row?.departureTime)) ? calculateTime(row.departureTime) : "--");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((row) => {
      const matchesSearch = !q || (row?.dayString || "").toLowerCase().includes(q) || locationOf(row).toLowerCase().includes(q);
      const matchesStatus = statusFilter === "all" || statusOf(row) === statusFilter;
      return matchesSearch && matchesStatus;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, search, statusFilter, fence]);

  // quick summary across the loaded history
  const summary = useMemo(() => ({
    days: data.length,
    present: data.filter((r) => statusOf(r) !== "Absent").length,
    late: data.filter((r) => statusOf(r) === "Late").length,
  }), [data]);

  const exportCsv = () => {
    const rows = filtered.map((row) => ({
      Date: row?.dayString || "", Status: statusOf(row), Location: locationOf(row),
      ClockIn: clockInOf(row), ClockOut: clockOutOf(row),
      BreakTime: getDuration(row?.breakHours || 0), TotalTime: getDuration(row?.totalHours || 0),
      EffectiveHrs: getDuration(row?.effectiveHours || 0),
    }));
    if (!rows.length) { notify.error("Nothing to export"); return; }
    const cfg = mkConfig({ useKeysAsHeaders: true, filename: `${name || "employee"}-attendance` });
    download(cfg)(generateCsv(cfg)(rows));
    notify.success(`Exported ${rows.length} day(s)`);
  };

  return (
    <Box>
      <TopBar MainHeading={"Attendance"}
        breadcrumbs={[{ label: "Attendance" }, { label: "Dashboard", href: "/app/attendance-dashboard" }, { label: "Employee Detail" }]} />

      {/* profile header */}
      <Box sx={{ background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px`, p: "1.25rem", mb: "1.25rem" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar src={employee?.profileImage || undefined} sx={{ width: "3rem", height: "3rem", bgcolor: tokens.accent, fontWeight: 600 }}>
              {initialsOf(name)}
            </Avatar>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>{name}</Box>
                {latestStatus && <StatusChip label={latestStatus} tone={STATUS_TONE[latestStatus]} />}
              </Stack>
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
                {[employee?.aliasId, role].filter(Boolean).join(" · ") || "—"}
              </Box>
            </Box>
          </Stack>
          <BtnSecondary to="/app/attendance-dashboard" sx={{ borderRadius: "8px" }}>
            Back
          </BtnSecondary>
        </Stack>
      </Box>

      <MainBox sx={{ padding: "1.25rem" }}>
        {/* summary chips */}
        {!loading && !error && (
          <Stack direction="row" spacing={3} sx={{ mb: "1.25rem", flexWrap: "wrap" }}>
            <Box><Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600 }}>{summary.days}</Box><Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Days recorded</Box></Box>
            <Box><Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.successText }}>{summary.present}</Box><Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Present</Box></Box>
            <Box><Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.warningText }}>{summary.late}</Box><Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Late</Box></Box>
          </Stack>
        )}

        {/* toolbar */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: "1.25rem" }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: "wrap" }}>
            <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search date or location" minWidth="15rem" />
            <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              options={[{ value: "all", label: "All statuses" }, { value: "Present", label: "Present" }, { value: "Late", label: "Late" }, { value: "Absent", label: "Absent" }]} />
          </Stack>
          <BtnSecondary onClick={exportCsv} sx={{ borderRadius: "8px" }}>
            <FileDownloadOutlinedIcon sx={{ fontSize: "1.125rem", mr: 0.5 }} /> Export CSV
          </BtnSecondary>
        </Stack>

        <DataTable
          rows={filtered}
          loading={loading}
          error={error}
          errorTitle="Couldn't load attendance"
          empty={{
            title: data.length === 0 ? "No attendance records" : "No matches",
            description: data.length === 0
              ? "This employee has no recorded attendance yet."
              : "Try a different search or filter.",
          }}
          getRowId={(row, i) => row._id || i}
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={attendanceDayColumns({
            statusOf, statusTone: STATUS_TONE, locationOf, clockInOf, clockOutOf, getDuration,
          })}
        />
      </MainBox>
    </Box>
  );
}
